import { FamilyUnit, PossibleEmployerContributionDto } from '@zorro/clients';
import { Group, Text } from '@zorro/web/design-system';
import { useImpersonation, useRoles } from '@zorro/web/ui-utils';

import { PersonalizedAllowanceAmountTooltip } from './PersonalizedAllowanceAmountTooltip';

const familyUnitConfig = {
  [FamilyUnit.EMPLOYEE_ONLY]: {
    getLabel: (isSelfView: boolean) =>
      isSelfView ? 'Only you:' : 'Employee only:',
  },
  [FamilyUnit.EMPLOYEE_SPOUSE]: {
    getLabel: (isSelfView: boolean) =>
      isSelfView ? 'You + spouse:' : 'Employee + spouse:',
  },
  [FamilyUnit.EMPLOYEE_CHILD]: {
    getLabel: (isSelfView: boolean) =>
      isSelfView ? 'You + child(ren):' : 'Employee + child(ren):',
  },
  [FamilyUnit.FAMILY]: {
    getLabel: (isSelfView: boolean) =>
      isSelfView ? 'Your family:' : `Employee's family:`,
  },
};

type PersonalizedAllowanceRowProps = {
  familyUnit: FamilyUnit;
  possibleEmployerContributions: PossibleEmployerContributionDto[];
};

export const PersonalizedAllowanceTooltip = ({
  familyUnit,
  possibleEmployerContributions,
}: PersonalizedAllowanceRowProps) => {
  const { isEmployeeOnlyRole } = useRoles();
  const { isImpersonated } = useImpersonation();
  const { getLabel } = familyUnitConfig[familyUnit];
  const label = getLabel(isEmployeeOnlyRole || isImpersonated);

  const isChildless =
    familyUnit === FamilyUnit.EMPLOYEE_ONLY ||
    familyUnit === FamilyUnit.EMPLOYEE_SPOUSE;

  const childlessEmployerContribution = isChildless
    ? possibleEmployerContributions.find(
        (model) => model.familyUnit === familyUnit
      )
    : null;

  const shouldNotRender = isChildless && !childlessEmployerContribution;

  const employerContributions = !isChildless
    ? possibleEmployerContributions.filter(
        (model) => model.familyUnit === familyUnit
      )
    : [];

  const minimumStipend = isChildless
    ? childlessEmployerContribution?.stipend
    : employerContributions.find((model) => model.numberOfDependents === 1)
        ?.stipend;

  const maximumStipend = !isChildless
    ? employerContributions.find((model) => model.numberOfDependents === null)
        ?.stipend
    : undefined;

  return shouldNotRender ? null : (
    <Group gap="0.25rem">
      <Text fz="xs">{label}</Text>
      <PersonalizedAllowanceAmountTooltip
        minimumStipend={minimumStipend}
        maximumStipend={maximumStipend}
      />
    </Group>
  );
};
